import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Briefcase, Calendar, Edit, FileText, Laptop, Mail } from 'lucide-react';
import { useState } from 'react';
import { CollapsibleCard } from '../components/CollapsibleCard';
import { EditJobInformationDialog } from '../components/dialogs/EditJobInformationDialog';
import type { ColorScheme, User, UserDetail } from '../types/user';
import { getThemeColors } from '../utils/themeColors';

interface JobInformationSectionProps {
  user: User;
  colorScheme: ColorScheme;
  userId?: string;
  userDetail?: UserDetail;
}

export function JobInformationSection({
  user,
  colorScheme,
  userId,
  userDetail,
}: JobInformationSectionProps) {
  const colors = getThemeColors(colorScheme);
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = !!userId;

  return (
    <div className="max-w-6xl space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`p-4 bg-gradient-to-br ${colors.stat1} border border-border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-medium ${colors.stat1Text} opacity-70 mb-1`}>
                Employee ID
              </p>
              <p className={`text-xl font-bold ${colors.stat1Text}`}>
                {userDetail?.employeeId || 'N/A'}
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-lg ${colors.stat1Icon} flex items-center justify-center`}
            >
              <Briefcase className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className={`p-4 bg-gradient-to-br ${colors.stat2} border border-border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-medium ${colors.stat2Text} opacity-70 mb-1`}>
                Start Date
              </p>
              <p className={`text-xl font-bold ${colors.stat2Text}`}>
                {userDetail?.startDate?.slice(0, 10) || 'N/A'}
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-lg ${colors.stat2Icon} flex items-center justify-center`}
            >
              <Calendar className="w-6 h-6" />
            </div>
          </div>
        </Card>

        <Card className={`p-4 bg-gradient-to-br ${colors.stat3} border border-border`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-xs font-medium ${colors.stat3Text} opacity-70 mb-1`}>
                Discipline
              </p>
              <p className={`text-xl font-bold ${colors.stat3Text}`}>
                {userDetail?.discipline || user.team || 'N/A'}
              </p>
            </div>
            <div
              className={`w-12 h-12 rounded-lg ${colors.stat3Icon} flex items-center justify-center`}
            >
              <FileText className="w-6 h-6" />
            </div>
          </div>
        </Card>
      </div>

      <CollapsibleCard
        title="Job Information"
        subtitle="Employment and role details"
        icon={Briefcase}
        iconBg="bg-gray-100 dark:bg-gray-900/30"
        iconColor="text-gray-600 dark:text-gray-400"
        actions={
          <Button
            variant="outline"
            size="sm"
            className="border-border"
            disabled={!canEdit}
            onClick={() => setEditOpen(true)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Edit
          </Button>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Job Title
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.jobTitle || user.role || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Discipline
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.discipline || user.team || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Skills
            </label>
            <div className="flex flex-wrap gap-1">
              {userDetail?.skills && userDetail.skills.length > 0 ? (
                userDetail.skills.map((skill: { id: number; name: string }) => (
                  <Badge
                    key={skill.id}
                    className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200"
                  >
                    {skill.name}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground italic">No skills assigned</span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Line Manager
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.lineManager || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Team Leader
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.teamLeader || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Teams
            </label>
            <div className="flex flex-wrap gap-1">
              {userDetail?.teams && userDetail.teams.length > 0 ? (
                userDetail.teams.map((t: { id: number; name: string }) => (
                  <Badge
                    key={t.id}
                    className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200"
                  >
                    {t.name}
                  </Badge>
                ))
              ) : (
                <span className="text-sm text-muted-foreground italic">No teams assigned</span>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Start Date
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.startDate?.slice(0, 10) || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Employment Type
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.employmentType || 'N/A'}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Work Contact Information"
        subtitle="Professional contact details"
        icon={Mail}
        iconBg="bg-green-100 dark:bg-green-900/30"
        iconColor="text-green-600 dark:text-green-400"
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Employee ID
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.employeeId || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Work Phone
            </label>
            <div className="text-sm text-foreground font-medium">
              {userDetail?.workPhone || 'N/A'}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Work Email
            </label>
            <div className="text-sm text-foreground font-medium break-all">
              {userDetail?.workEmail || 'N/A'}
            </div>
          </div>
        </div>
      </CollapsibleCard>

      <CollapsibleCard
        title="Mobile Access"
        subtitle="Mobile application access status"
        icon={Laptop}
        iconBg="bg-purple-100 dark:bg-purple-900/30"
        iconColor="text-purple-600 dark:text-purple-400"
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              User Verified
            </label>
            <Badge
              variant="outline"
              className={
                userDetail?.verified
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                  : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300'
              }
            >
              {userDetail?.verified ? 'Verified' : 'Not Verified'}
            </Badge>
          </div>
          <div className="space-y-2">
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Mobile Status
            </label>
            <Badge
              variant="outline"
              className={
                user.isMobileUser
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
              }
            >
              {user.isMobileUser ? 'Enabled' : 'Disabled'}
            </Badge>
          </div>
        </div>
      </CollapsibleCard>

      {canEdit && userId && (
        <EditJobInformationDialog
          open={editOpen}
          onOpenChange={setEditOpen}
          userId={userId}
          user={userDetail}
        />
      )}
    </div>
  );
}
